import { OTLPConfig, SeverityNumber } from '@thoughtspot/observability';

const SERVICE_NAME = 'blink-v2';
const otelEndpoint = '/otel/v1';

// Operations catalogue -
// Single source of truth for every GraphQL operation the frontend tracing
// layer knows about. The 'propagated' flag is the only behavioural axis:
//
//   propagated: true  — Browser creates a CLIENT span around the fetch and
//                       injects B3 headers; prism's SERVER span stitches
//                       into the same trace. The fetch wrapper gates on
//                       PROPAGATED_OPERATIONS for this.
//
//   propagated: false — Craetes a leaf span for a gql operation. i.e. — no backend children,
//                       just the browser-side timing. If we make it true, then the operation
//                       will get traced in prism as well.
//
// Note - Operation names MUST match the 'operation' declaration in the corresponding gql file
const OPERATIONS = {
    GetChartWithData: { propagated: true },
    GetTableWithData: { propagated: true },
    GetTableWithHeadlineData: { propagated: true },
    GetAnswerDataByQuery: { propagated: true },
    GetVizData: { propagated: false },
    LoadAnswerSession: { propagated: false },
    LoadExistingAnswerSession: { propagated: false },
    UpdateChartClientState: { propagated: false },
    LoadPinboardSession: { propagated: false },
    LoadContextBook: { propagated: false },
} as const;

// Typed union of every operation name in the catalogue.
export type TracedOperation = keyof typeof OPERATIONS;

// Typed name accessor for call sites: ex - TRACED_OPERATION.GetChartWithData.
export const TRACED_OPERATION = Object.fromEntries(
    Object.keys(OPERATIONS).map(k => [k, k]),
) as { readonly [K in TracedOperation]: K };

// Subset of OPERATIONS that also injects B3 headers so prism span joins the trace.
export const PROPAGATED_OPERATIONS: ReadonlySet<string> = new Set(
    (Object.keys(OPERATIONS) as TracedOperation[]).filter(
        k => OPERATIONS[k].propagated,
    ),
);

// Labels to identify the originating component in trace data
export const TRACE_COMPONENT = {
    AppView: 'AppView_render',
    AnswerContentContainer: 'AnswerContentContainer_render',
    ChartVizContainer: 'ChartVizContainer_render',
    TableVizContainer: 'TableVizContainer_render',
    HeadlineVizContainer: 'HeadlineVizContainer_render',
    CustomChartDataFetch: 'custom_chart_data_fetch',
    ValidatedPinboard: 'ValidatedPinboard_render',
    PinboardContextBookLoader: 'PinboardContextBookLoader_render',
    PinboardVizContainer: 'PinboardVizContainer_render',
    VizEmbedPage: 'VizEmbedPage_render',
} as const;

export type TraceComponent = typeof TRACE_COMPONENT[keyof typeof TRACE_COMPONENT];

export const OTLP_CONFIG: OTLPConfig = {
    traces: {
        url: `${otelEndpoint}/traces`,
        flushConfig: {
            flushIntervalMs: 10000,
            maxQueueSize: 1000,
            // Keep export bodies small enough to pass through proxy
            // request-size limits; spans are sent as uncompressed JSON.
            maxExportBatchSize: 50,
            flushOnFull: true,
        },
    },
    metrics: {
        url: `${otelEndpoint}/metrics`,
        flushConfig: {
            flushIntervalMs: 15000,
            maxQueueSize: 1000,
            flushOnFull: true,
        },
    },
    logs: {
        url: `${otelEndpoint}/logs`,
        flushConfig: {
            flushIntervalMs: 5000,
            maxQueueSize: 200,
            flushOnFull: true,
        },
        severityMap: {
            [SeverityNumber.UNSPECIFIED]: 'otlp',
            [SeverityNumber.ERROR]: ['console', 'otlp'],
            [SeverityNumber.WARN]: ['console', 'otlp'],
        },
    },
    serviceName: SERVICE_NAME,
};
